import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Row, Col, Card, Button, Table, message, Drawer, Popover, Popconfirm } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	getCurrency,
	getCurrencyIdIFSamePRLineItemCurrency,
	getEntityById,
	getPurchaseRequestId,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
	renderCurrencyByCurrencyId,
	roundOffToDecimal,
	stopDefaultEvents,
} from '../../utils/DataAccessUtils';
import {
	patchPurchaseRequest,
	purchaseRequestsRestCrudThunksForSupplier,
} from '../../thunks/purchase_requests_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ContactRowDisplay } from '../contact_row_display/ContactRowDisplay';
import { AddressDisplay } from '../address_display/AddressDisplay';
import { HorizontalKeyValueDisplay } from '../horizontal_key_value_display/HorizontalKeyValueDisplay';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';
import PartLineItemRowDisplay from '../parts_line_item_row_display/PartLineItemRowDisplay';
import EquipmentLineItemRowDisplay from '../equipment_line_item_row_display/EquipmentLineItemRowDisplay';
import {
	EDITABLE_PR_STATUSES,
	PURCHASE_REQUEST_STATUSES,
} from '../../constants/PurchaseRequestStatuses';
import PRWarrantyAssetDetail from './PRWarrantyAssetDetail';
import PurchaseRequestStatusActions from './PurchaseRequestStatusActions';
import { CONFIG_NAMES } from '../../utils/AuthUtils';
import { isSupplierInventoryConfigEnabled } from '../common/SupplierInventoryConfigPermissionChecker';
import { EditOutlined } from '@ant-design/icons';
import PartForm from '../part_form/PartForm';
import EquipmentForm from '../equipment_form/EquipmentForm';
import DateTimeHover from '../date_time_component/DateTime';
import ReplacePartLineItemModal from '../purchase_order_form/ReplacePartLineItemModal';
import ReplaceEquipmentLineItemModal from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import { removeLineItemFromPR } from '../../thunks/purchase_request_line_items_thunks';
import OWEditableNotesComponent from '../common/OWEditableNotesComponent';
import ApprovalHierarchyDisplayPurchaseRequest from '../approval_hierarchy_display_purchase_request/ApprovalHierarchyDisplayPurchaseRequest';
import { canMarkPROrderedOrFulfilled } from '../../utils/InventoryUtils';
import PurchaseRequestOrderedOrFulfilledSection from './PurchaseRequestOrderedOrFulfilledSection';

const { Content } = Layout;

require('./PurchaseRequestsDetailDetailsPage.less');
const DEFAULT_BACK_LINK_TEXT = 'Back to purchase request';

const PurchaseRequestsDetailPage: FC<any> = ({
	getPurchaseRequest,
	match,
	purchaseRequest,
	history,
	purchaseRequestsFetching,
	location,
	currentUser,
	companyConfig,
	removeLineItem,
	patchPurchaseRequest,
}) => {
	const [currentlyEditingPart, setCurrentlyEditingPart] = useState(null);
	const [partEditDrawerVisible, setPartEditDrawerVisible] = useState(false);
	const [currentlyEditingEquipment, setCurrentlyEditingEquipment] = useState(null);
	const [equipmentEditDrawerVisible, setEquipmentEditDrawerVisible] = useState(false);
	const [refreshing, setRefreshing] = useState(false);

	const [editingLineItem, setEditingLineItem] = useState(null);
	const [replacePartModalVisible, setReplacePartModalVisible] = useState(false);
	const [replaceEquipmentModalVisible, setReplaceEquipmentModalVisible] = useState(false);
	const [removingLineItem, setRemovingLineItem] = useState(false);

	const fetchPurchaseRequest = useCallback(() => {
		setRefreshing(true);
		getPurchaseRequest(match.params.id).finally(() => setRefreshing(false));
	}, [getPurchaseRequest, match]);

	const handleSaveNotes = useCallback(
		(notes) => patchPurchaseRequest(match.params.id, { internalNotes: notes }),
		[patchPurchaseRequest, match]
	);

	useEffect(() => {
		if (!nullSafeGet('id', purchaseRequest)) {
			fetchPurchaseRequest();
		}
	}, []);

	const shipToLocation = useMemo(() => !!purchaseRequest.locationId, [purchaseRequest.locationId]);

	const canEdit = useMemo(
		() => EDITABLE_PR_STATUSES.includes(purchaseRequest.status),
		[purchaseRequest.status]
	);

	const prCurrencyId = useMemo(
		() => getCurrencyIdIFSamePRLineItemCurrency(purchaseRequest),
		[purchaseRequest]
	);

	const purchaseRequestDetails = useMemo(
		() => ({
			'PR #': getPurchaseRequestId(purchaseRequest),
			'Requested by': (
				<ContactRowDisplay
					contact={purchaseRequest.createdByContact}
					userType={ROLE_TYPES.SUPPLIER}
				/>
			),
			...(purchaseRequest.workOrder && {
				'Work Order ID': (
					<HyperLink
						text={`# ${purchaseRequest.workOrder.id}`}
						entityType={ENTITY_TYPE.WORK_ORDER}
						entityId={nullSafeGet('workOrder.id', purchaseRequest)}
						backLinkText={DEFAULT_BACK_LINK_TEXT}
					/>
				),
			}),
			'Ship to': (
				<HyperLink
					text={nullSafeGet(
						`${shipToLocation ? 'location' : 'stockLocation'}.name`,
						purchaseRequest
					)}
					entityType={shipToLocation ? ENTITY_TYPE.LOCATION : ENTITY_TYPE.STOCK_LOCATION}
					entityId={nullSafeGet(
						`${shipToLocation ? 'locationId' : 'stockLocationId'}`,
						purchaseRequest
					)}
					backLinkText={DEFAULT_BACK_LINK_TEXT}
				/>
			),
			'Shipping address': (
				<AddressDisplay
					address={
						nullSafeGet('location.buyerFacility.primaryAddress', purchaseRequest) ||
						nullSafeGet('stockLocation.address', purchaseRequest) ||
						nullSafeGet('stockLocation.location.buyerFacility.primaryAddress', purchaseRequest)
					}
				/>
			),
			...(prCurrencyId && {
				'Total cost': renderCurrencyByCurrencyId(prCurrencyId)(purchaseRequest.totalCost),
			}),
			'Requested at': <DateTimeHover timestamp={nullSafeGet('createdAt', purchaseRequest)} />,
			...(purchaseRequest.warrantyAssetId && {
				'Warranty Asset': (
					<PRWarrantyAssetDetail
						id={purchaseRequest.warrantyAssetId}
						asset={purchaseRequest.warrantyAsset}
						backLinkText={DEFAULT_BACK_LINK_TEXT}
						timeZoneId={nullSafeGet('location.timeZoneId', purchaseRequest)}
					/>
				),
			}),
			...(!purchaseRequest.warrantyAssetId &&
				nullSafeGet('workOrder.assetId', purchaseRequest) && {
					'WO Asset': (
						<HyperLink
							text={nullSafeGetOrElse(
								'workOrder.asset.name',
								purchaseRequest,
								`# ${nullSafeGet('workOrder.assetId', purchaseRequest)}`
							)}
							entityType={ENTITY_TYPE.ASSET_DETAIL}
							entityId={nullSafeGet('workOrder.assetId', purchaseRequest)}
							backLinkText={DEFAULT_BACK_LINK_TEXT}
						/>
					),
				}),
			...(purchaseRequest.approvedOrDeclinedOrCancelledByContact && {
				[`${
					purchaseRequest.status === PURCHASE_REQUEST_STATUSES.denied
						? 'Denied'
						: purchaseRequest.status === PURCHASE_REQUEST_STATUSES.cancelled
						? 'Cancelled'
						: 'Approved'
				} by`]: (
					<ContactRowDisplay
						contact={purchaseRequest.approvedOrDeclinedOrCancelledByContact}
						userType={ROLE_TYPES.SUPPLIER}
					/>
				),
			}),
		}),
		[purchaseRequest, shipToLocation, prCurrencyId]
	);

	const onEditPRLineItem = useCallback(
		(e) => {
			stopDefaultEvents(e);
			history.push(`/supplier/purchaseRequests/edit/${purchaseRequest.id}`);
		},
		[history, purchaseRequest.id]
	);

	const userCurrencyId = useMemo(
		() => nullSafeGet('id', getCurrency({ currentUser })),
		[currentUser]
	);

	const editPart = useCallback(
		(lineItem) => (e) => {
			stopDefaultEvents(e);
			setCurrentlyEditingPart(lineItem.part);
			setEditingLineItem(lineItem);
			setPartEditDrawerVisible(true);
		},
		[]
	);

	const onPartEditClose = useCallback(() => {
		setPartEditDrawerVisible(false);
		setCurrentlyEditingPart(null);
		setEditingLineItem(null);
	}, []);

	const onPartEditSuccess = useCallback(() => {
		onPartEditClose();
		fetchPurchaseRequest();
	}, [fetchPurchaseRequest, onPartEditClose]);

	const onReplacePartLineItemSuccess = useCallback(() => {
		_hideReplacePartModal();
		onPartEditClose();
		fetchPurchaseRequest();
	}, [onPartEditClose, fetchPurchaseRequest]);

	const partsColumns = useMemo(
		() => [
			{
				title: 'Part Number',
				dataIndex: ['part'],
				render: (part, record) => (
					<div className="flex flex-row items-center">
						<div>{nullSafeGet('partNumber', part) || '--'}</div>
						{record.isDeleted ? null : (
							<Button type="link" icon={<EditOutlined />} onClick={editPart(record)} />
						)}
					</div>
				),
			},
			{
				title: 'Part',
				dataIndex: 'partId',
				render: (_, record) => <PartLineItemRowDisplay partLineItem={record} />,
			},
			{
				title: 'Vendor Name',
				render: (_, record) =>
					nullSafeGetOrElse('partCatalog.partEquipmentVendor.name', record, '--'),
			},
			{
				title: 'Quantity',
				dataIndex: 'partQuantity',
			},
			{
				title: 'Unit Cost',
				dataIndex: 'partCost',
				render: (_, record) => {
					const unitCost =
						_ && record.partQuantity ? roundOffToDecimal(_ / record.partQuantity) : undefined;
					const currencyId = nullSafeGetOrElse(
						'partCatalog.partEquipmentVendor.currencyId',
						record,
						userCurrencyId
					);
					return renderCurrencyByCurrencyId(currencyId)(unitCost);
				},
			},
			{
				title: 'Total Cost',
				dataIndex: 'partCost',
				render: (_, record) => {
					const currencyId = nullSafeGetOrElse(
						'partCatalog.partEquipmentVendor.currencyId',
						record,
						userCurrencyId
					);
					return renderCurrencyByCurrencyId(currencyId)(_);
				},
			},
			{
				render: (_, record) =>
					record.isDeleted ? null : (
						<Button type="ghost" onClick={onEditPRLineItem} disabled={!canEdit}>
							Edit
						</Button>
					),
			},
		],
		[canEdit, editPart, onEditPRLineItem, userCurrencyId]
	);

	const editEquipment = useCallback(
		(lineItem) => (e) => {
			stopDefaultEvents(e);
			setCurrentlyEditingEquipment(lineItem.equipmentType);
			setEditingLineItem(lineItem);
			setEquipmentEditDrawerVisible(true);
		},
		[]
	);

	const onEquipmentEditClose = useCallback(() => {
		setEquipmentEditDrawerVisible(false);
		setCurrentlyEditingEquipment(null);
		setEditingLineItem(null);
	}, []);

	const onEquipmentEditSuccess = useCallback(() => {
		onEquipmentEditClose();
		fetchPurchaseRequest();
	}, [fetchPurchaseRequest, onEquipmentEditClose]);

	const onReplaceEquipmentLineItemSuccess = useCallback(() => {
		_hideReplaceEquipmentModal();
		onEquipmentEditClose();
		fetchPurchaseRequest();
	}, [onEquipmentEditClose, fetchPurchaseRequest]);

	const removePartOrEquipmentLineItem = useCallback(
		(type) => () => {
			setRemovingLineItem(true);
			removeLineItem(editingLineItem.id)
				.then(() => {
					message.success('Removed successfully!');
					type === 'part' ? onPartEditSuccess() : onEquipmentEditSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setRemovingLineItem(false));
		},
		[editingLineItem, onEquipmentEditSuccess, onPartEditSuccess, removeLineItem]
	);

	const equipmentColumns = useMemo(
		() => [
			{
				title: 'Equipment Type #',
				dataIndex: ['equipmentType'],
				render: (equipment, record) => (
					<div className="flex flex-row items-center">
						<div>{nullSafeGet('equipmentTypeNumber', equipment) || '--'}</div>
						{record.isDeleted ? null : (
							<Button type="link" icon={<EditOutlined />} onClick={editEquipment(record)} />
						)}
					</div>
				),
			},
			{
				title: 'Equipment',
				dataIndex: 'equipmentTypeNumber',
				render: (_, record) => <EquipmentLineItemRowDisplay equipmentLineItem={record} />,
			},
			{
				title: 'Vendor Name',
				render: (_, record) =>
					nullSafeGetOrElse('equipmentCatalog.partEquipmentVendor.name', record, '--'),
			},
			{
				title: 'Quantity',
				dataIndex: 'equipmentQuantity',
			},
			{
				title: 'Unit Cost',
				dataIndex: 'equipmentCost',
				render: (_, record) => {
					const unitCost =
						_ && record.equipmentQuantity
							? roundOffToDecimal(_ / record.equipmentQuantity)
							: undefined;
					const currencyId = nullSafeGetOrElse(
						'equipmentCatalog.partEquipmentVendor.currencyId',
						record,
						userCurrencyId
					);
					return renderCurrencyByCurrencyId(currencyId)(unitCost);
				},
			},
			{
				title: 'Total Cost',
				dataIndex: 'equipmentCost',
				render: (_, record) => {
					const currencyId = nullSafeGetOrElse(
						'equipmentCatalog.partEquipmentVendor.currencyId',
						record,
						userCurrencyId
					);
					return renderCurrencyByCurrencyId(currencyId)(_);
				},
			},
			{
				render: (_, record) =>
					record.isDeleted ? null : (
						<Button type="ghost" onClick={onEditPRLineItem} disabled={!canEdit}>
							Edit
						</Button>
					),
			},
		],
		[canEdit, editEquipment, onEditPRLineItem, userCurrencyId]
	);

	const openEquipmentDetail = useCallback(
		(record) => {
			const equipmentTypeId = nullSafeGet('equipmentCatalog.equipmentType.id', record);
			const path = `/supplier/equipments/detail/${equipmentTypeId}`;
			equipmentTypeId &&
				history.push(getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path));
		},
		[history, location]
	);

	const equipmentOnRow = (record) => ({
		onClick: () => openEquipmentDetail(record),
	});

	const openPartDetail = useCallback(
		(record) => {
			const path = `/supplier/parts/detail/${record.part.id}`;
			history.push(getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path));
		},
		[history, location]
	);

	const partsOnRow = (record) => ({
		onClick: () => openPartDetail(record),
	});

	const replacePartLineItem = () => setReplacePartModalVisible(true);
	const _hideReplacePartModal = () => setReplacePartModalVisible(false);

	const replaceEquipmentLineItem = () => setReplaceEquipmentModalVisible(true);
	const _hideReplaceEquipmentModal = () => setReplaceEquipmentModalVisible(false);

	const fetching = refreshing || (purchaseRequestsFetching && !nullSafeGet('id', purchaseRequest));

	const canReplaceOrRemove = useMemo(
		() => EDITABLE_PR_STATUSES.includes(nullSafeGet('status', editingLineItem)),
		[editingLineItem]
	);

	const allPartLineItems = useMemo(
		() => [
			...nullSafeGetOrElse('partSupplierPurchaseRequestLineItems', purchaseRequest, []),
			...nullSafeGetOrElse('deletedPartSupplierPurchaseRequestLineItems', purchaseRequest, []),
		],
		[purchaseRequest]
	);

	const allEquipmentLineItems = useMemo(
		() => [
			...nullSafeGetOrElse('equipmentSupplierPurchaseRequestLineItems', purchaseRequest, []),
			...nullSafeGetOrElse('deletedEquipmentSupplierPurchaseRequestLineItems', purchaseRequest, []),
		],
		[purchaseRequest]
	);

	return fetching ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="purchaseRequestsDetailDetailsPage">
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24} className="rowSpacing">
					{isSupplierInventoryConfigEnabled(companyConfig, CONFIG_NAMES.DISABLE_PURCHASE_ORDERS) ? (
						<PurchaseRequestStatusActions
							purchaseRequest={purchaseRequest}
							onSuccess={fetchPurchaseRequest}
						/>
					) : purchaseRequest.status === PURCHASE_REQUEST_STATUSES.requested ? (
						<Card>
							<ApprovalHierarchyDisplayPurchaseRequest
								purchaseRequest={purchaseRequest}
								refresh={fetchPurchaseRequest}
							/>
						</Card>
					) : null}
				</Col>
				<Col span={18} className="rowSpacing">
					<Card>
						{canMarkPROrderedOrFulfilled(purchaseRequest, companyConfig) ? (
							<PurchaseRequestOrderedOrFulfilledSection
								purchaseRequest={purchaseRequest}
								onSuccess={fetchPurchaseRequest}
							/>
						) : null}
						<HorizontalKeyValueDisplay
							rowStyle={{ minWidth: '25%' }}
							keyValueStore={purchaseRequestDetails}
						/>
					</Card>
				</Col>
				<Col span={6} className="rowSpacing">
					<div style={{ height: '100%' }}>
						<OWEditableNotesComponent
							style={{ height: '100%' }}
							loading={fetching}
							id="prNotes"
							tooltipMessage=""
							title="Notes"
							allowEdit={true}
							notes={nullSafeGetOrElse('internalNotes', purchaseRequest, '')}
							onSaveNotes={handleSaveNotes}
						/>
					</div>
				</Col>
				{allPartLineItems.length > 0 ? (
					<Col span={24} className="rowSpacing">
						<Card title={`Parts`}>
							<Table
								columns={partsColumns}
								rowKey={(partLineItem) => partLineItem['partCatalogId']}
								dataSource={allPartLineItems}
								showHeader
								loading={false}
								onRow={onRowWithRangeSelection(partsOnRow)}
							/>
						</Card>
					</Col>
				) : null}
				{allEquipmentLineItems.length > 0 ? (
					<Col span={24} className="rowSpacing">
						<Card title={`Equipment`}>
							<Table
								columns={equipmentColumns}
								rowKey={(equipment) => equipment['id']}
								dataSource={allEquipmentLineItems}
								showHeader
								loading={false}
								onRow={onRowWithRangeSelection(equipmentOnRow)}
							/>
						</Card>
					</Col>
				) : null}
			</Row>
			{partEditDrawerVisible ? (
				<Drawer
					width="50%"
					placement="right"
					closable={true}
					onClose={onPartEditClose}
					visible={true}
					extra={
						<div className="flex flex-row items-center">
							<Popover
								content={
									!canReplaceOrRemove ? 'Ordered items can be removed from Purchase Order' : ''
								}
								trigger="hover"
							>
								<Popconfirm
									onConfirm={removePartOrEquipmentLineItem('part')}
									placement="topLeft"
									title="Are you sure you want to remove?"
								>
									<Button type="ghost" loading={removingLineItem} disabled={!canReplaceOrRemove}>
										Remove Part
									</Button>
								</Popconfirm>
							</Popover>
							<div className="ml-2">
								<Popover
									content={
										!canReplaceOrRemove ? 'Ordered items can be replaced from Purchase Order' : ''
									}
									trigger="hover"
								>
									<Button type="link" disabled={!canReplaceOrRemove} onClick={replacePartLineItem}>
										Replace Part
									</Button>
								</Popover>
							</div>
						</div>
					}
				>
					<PartForm
						onCancel={onPartEditClose}
						formData={currentlyEditingPart}
						onSuccess={onPartEditSuccess}
					/>
				</Drawer>
			) : null}
			{equipmentEditDrawerVisible ? (
				<Drawer
					width="50%"
					placement="right"
					closable={true}
					onClose={onEquipmentEditClose}
					visible={true}
					extra={
						<div className="flex flex-row items-center">
							<Popover
								content={!canReplaceOrRemove ? 'Received items cannot be removed' : ''}
								trigger="hover"
							>
								<Popconfirm
									onConfirm={removePartOrEquipmentLineItem('equipment')}
									placement="topLeft"
									title="Are you sure you want to remove?"
								>
									<Button type="ghost" loading={removingLineItem} disabled={!canReplaceOrRemove}>
										Remove Equipment
									</Button>
								</Popconfirm>
							</Popover>
							<div className="ml-2">
								<Popover
									content={!canReplaceOrRemove ? 'Received items cannot be replaced' : ''}
									trigger="hover"
								>
									<Button
										type="link"
										disabled={!canReplaceOrRemove}
										onClick={replaceEquipmentLineItem}
									>
										Replace Equipment
									</Button>
								</Popover>
							</div>
						</div>
					}
				>
					<EquipmentForm
						onCancel={onEquipmentEditClose}
						formData={currentlyEditingEquipment}
						onSuccess={onEquipmentEditSuccess}
					/>
				</Drawer>
			) : null}
			{replacePartModalVisible ? (
				<ReplacePartLineItemModal
					isPRLineItem
					lineItem={editingLineItem}
					vendorId={nullSafeGet('partCatalog.partEquipmentVendorId', editingLineItem)}
					onCancel={_hideReplacePartModal}
					onSuccess={onReplacePartLineItemSuccess}
				/>
			) : null}
			{replaceEquipmentModalVisible ? (
				<ReplaceEquipmentLineItemModal
					isPRLineItem
					lineItem={editingLineItem}
					vendorId={nullSafeGet('equipmentCatalog.partEquipmentVendorId', editingLineItem)}
					onCancel={_hideReplaceEquipmentModal}
					onSuccess={onReplaceEquipmentLineItemSuccess}
				/>
			) : null}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	purchaseRequestsFetching: state.purchase_requests.fetching,
	purchaseRequest: getEntityById(ownProps.match.params.id, state.purchase_requests),
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	location: ownProps.location,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	getPurchaseRequest: (id) => dispatch(purchaseRequestsRestCrudThunksForSupplier.readOne(id)),
	patchPurchaseRequest: (id, partialFields) =>
		dispatch(patchPurchaseRequest(ROLE_TYPES.SUPPLIER)(id, partialFields)),
	removeLineItem: (id) => dispatch(removeLineItemFromPR(id)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PurchaseRequestsDetailPage)
);
